import React from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Icon,
  IconButton,
  Tooltip,
  alpha,
  Drawer,
  Divider,
  Stack,
  Chip,
} from "@mui/material";
import { toast } from "react-toastify";
import {
  processedItemsService,
  ProcessedItem,
} from "../../../services/processed-items.service";
import { DeleteConfirmationDialog } from "../../../components/DeleteConfirmationDialog/DeleteConfirmationDialog";
import { EmptyState } from "../../../../../components/EmptyState/EmptyState";
import { PageHeader } from "../../../components/PageHeader/PageHeader";
import { ProcessedItemFormDialog } from "./ProcessedItemFormDialog";
import { ProcessedItemDetailsDrawer } from "./ProcessedItemDetailsDrawer";
import { IESDoc, FilterGroup, TableColumn } from "../../../../../app.interface";
import { AppTable } from "../../../../../components/AppTable";
import { AppSearch } from "../../../../../components/AppSearch";
import dayjs from "dayjs";
import { hasPermission } from "../../../../auth/utils/permission.utils";
import { useAuth } from "../../../../auth/hooks/useAuth";

type ProcessedItemDoc = IESDoc<ProcessedItem>;

export const ProcessedItemsManager: React.FC = () => {
  const { user } = useAuth();
  const [processedItems, setProcessedItems] = React.useState<
    ProcessedItemDoc[]
  >([]);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [formDialogOpen, setFormDialogOpen] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [selectedItem, setSelectedItem] =
    React.useState<ProcessedItemDoc | null>(null);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(25);
  const [totalItems, setTotalItems] = React.useState(0);
  const [sortField, setSortField] = React.useState<string>("created_at");
  const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">("desc");
  const [search, setSearch] = React.useState("");
  const [filters, setFilters] = React.useState<Record<string, any>>({});
  const [detailsDrawerOpen, setDetailsDrawerOpen] = React.useState(false);

  const filterGroups: FilterGroup[] = [
    {
      id: "category",
      label: "Category",
      type: "select",
      icon: "category",
      description: "Filter items by their category",
      options: [
        {
          id: "main_course",
          label: "Main Course",
          value: "main_course",
          icon: "restaurant",
          color: "#0891B2",
        },
        {
          id: "appetizer",
          label: "Appetizer",
          value: "appetizer",
          icon: "tapas",
          color: "#0891B2",
        },
        {
          id: "dessert",
          label: "Dessert",
          value: "dessert",
          icon: "cake",
          color: "#0891B2",
        },
        {
          id: "beverage",
          label: "Beverage",
          value: "beverage",
          icon: "local_bar",
          color: "#0891B2",
        },
      ],
    },
    {
      id: "status",
      label: "Status",
      type: "select",
      icon: "fiber_manual_record",
      description: "Filter items by their current status",
      options: [
        {
          id: "active",
          label: "Active",
          value: "active",
          icon: "check_circle",
          color: "#059669",
        },
        {
          id: "inactive",
          label: "Inactive",
          value: "inactive",
          icon: "cancel",
          color: "#DC2626",
        },
      ],
    },
    {
      id: "sortBy",
      label: "Sort By",
      type: "select",
      icon: "sort",
      description: "Sort items by field",
      options: [
        {
          id: "name_asc",
          label: "Name (A-Z)",
          value: "name_asc",
          icon: "arrow_upward",
        },
        {
          id: "name_desc",
          label: "Name (Z-A)",
          value: "name_desc",
          icon: "arrow_downward",
        },
        {
          id: "created_asc",
          label: "Created (Oldest)",
          value: "created_asc",
          icon: "schedule",
        },
        {
          id: "created_desc",
          label: "Created (Newest)",
          value: "created_desc",
          icon: "update",
        },
      ],
    },
  ];

  const MasterColumns: TableColumn[] = [
    {
      id: "basic_info",
      header: "Item Details",
      field: "name",
      type: "action",
      width: "250px",
      renderAction: (row: any) => (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {row.name}
            </Typography>
            <Chip
              size="small"
              label={row.api_batch_id}
              sx={{
                bgcolor: alpha("#0891B2", 0.08),
                color: "#0891B2",
                fontWeight: 500,
                fontSize: "0.75rem",
              }}
            />
          </Box>
          {row.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {row.description}
            </Typography>
          )}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                px: 1,
                py: 0.25,
                borderRadius: 1,
                bgcolor: alpha("#0891B2", 0.08),
                color: "#0891B2",
                fontSize: "0.75rem",
                fontWeight: 500,
              }}
            >
              <Icon sx={{ fontSize: 16 }}>category</Icon>
              {row.category || "Uncategorized"}
            </Box>
          </Box>
        </Box>
      ),
    },
    {
      id: "stock",
      header: "Stock & Unit",
      field: "remaining_quantity",
      type: "action",
      width: "200px",
      renderAction: (row: any) => {
        const stockPercentage = (row.remaining_quantity / row.quantity) * 100;
        const getStockColor = () => {
          if (stockPercentage === 0) return "#DC2626";
          if (stockPercentage <= 20) return "#F59E0B";
          if (stockPercentage <= 50) return "#0891B2";
          return "#059669";
        };

        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Icon sx={{ color: getStockColor(), fontSize: 20 }}>
                {stockPercentage === 0
                  ? "error"
                  : stockPercentage <= 20
                  ? "warning"
                  : "inventory"}
              </Icon>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {row.remaining_quantity}/{row.quantity} {row.unit}
              </Typography>
            </Box>
            <Box
              sx={{
                width: "100%",
                height: 4,
                bgcolor: alpha(getStockColor(), 0.2),
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  width: `${stockPercentage}%`,
                  height: "100%",
                  bgcolor: getStockColor(),
                  transition: "width 0.3s ease",
                }}
              />
            </Box>
          </Box>
        );
      },
    },
    {
      id: "cost",
      header: "Cost Information",
      field: "cost_per_unit",
      type: "action",
      width: "180px",
      renderAction: (row: any) => (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Icon sx={{ color: "#0891B2", fontSize: 16 }}>payments</Icon>
            <Typography variant="body2">
              {row.cost_per_unit.toFixed(3)} BHD/{row.unit}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Total: {row.total_cost.toFixed(3)} BHD
          </Typography>
        </Box>
      ),
    },
    {
      id: "dates",
      header: "Production & Expiry",
      field: "production_date",
      type: "action",
      width: "200px",
      renderAction: (row: any) => {
        const isExpired = dayjs(row.expiry_date).isBefore(dayjs());
        const isExpiring = dayjs(row.expiry_date).isBefore(
          dayjs().add(7, "day")
        );

        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Icon sx={{ color: "#0891B2", fontSize: 16 }}>event</Icon>
              <Typography variant="caption">
                Produced: {dayjs(row.production_date).format("MMM D, YYYY")}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Icon
                sx={{
                  color: isExpired
                    ? "#DC2626"
                    : isExpiring
                    ? "#F59E0B"
                    : "#059669",
                  fontSize: 16,
                }}
              >
                {isExpired
                  ? "error"
                  : isExpiring
                  ? "warning"
                  : "event_available"}
              </Icon>
              <Typography
                variant="caption"
                sx={{
                  color: isExpired
                    ? "#DC2626"
                    : isExpiring
                    ? "#F59E0B"
                    : "text.secondary",
                }}
              >
                Expires: {dayjs(row.expiry_date).format("MMM D, YYYY")}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      id: "ingredients",
      header: "Ingredients",
      field: "ingredients",
      type: "action",
      width: "180px",
      renderAction: (row: any) => (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {row.ingredients?.length || 0} ingredient
            {row.ingredients?.length !== 1 ? "s" : ""}
          </Typography>
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {row.ingredients?.slice(0, 2).map((ing: any, index: number) => (
              <Chip
                key={index}
                size="small"
                label={`${ing.quantity} ${ing.unit}`}
                sx={{
                  bgcolor: alpha("#0891B2", 0.08),
                  color: "#0891B2",
                  fontWeight: 500,
                  fontSize: "0.75rem",
                }}
              />
            ))}
            {(row.ingredients?.length || 0) > 2 && (
              <Chip
                size="small"
                label={`+${row.ingredients.length - 2} more`}
                sx={{
                  bgcolor: alpha("#0891B2", 0.08),
                  color: "#0891B2",
                  fontWeight: 500,
                  fontSize: "0.75rem",
                }}
              />
            )}
          </Box>
        </Box>
      ),
    },
    {
      id: "actions",
      header: "",
      field: "actions",
      type: "action",
      width: "100px",
      renderAction: (row: any) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Edit Item" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleEditItem(row);
              }}
              disabled={
                !hasPermission(
                  "inventory.api.edit",
                  user?.newPermissions,
                  user?.assignment_details?.id,
                  user?.is_store_owner
                )
              }
              sx={{
                width: 34,
                height: 34,
                bgcolor: alpha("#0891B2", 0.08),
                color: "#0891B2",
                backdropFilter: "blur(8px)",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-1px)",
                  bgcolor: alpha("#0891B2", 0.12),
                  boxShadow: "0 4px 8px rgba(8, 145, 178, 0.15)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              <Icon sx={{ fontSize: 18 }}>edit_outlined</Icon>
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Item" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenDeleteDialog(row);
              }}
              disabled={
                !hasPermission(
                  "inventory.api.delete",
                  user?.newPermissions,
                  user?.assignment_details?.id,
                  user?.is_store_owner
                )
              }
              sx={{
                width: 34,
                height: 34,
                bgcolor: alpha("#DC2626", 0.08),
                color: "#DC2626",
                backdropFilter: "blur(8px)",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-1px)",
                  bgcolor: alpha("#DC2626", 0.12),
                  boxShadow: "0 4px 8px rgba(220, 38, 38, 0.15)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              <Icon sx={{ fontSize: 18 }}>delete_outlined</Icon>
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  React.useEffect(() => {
    loadProcessedItems();
  }, [page, pageSize, sortField, sortOrder, search, filters]);

  const loadProcessedItems = async () => {
    try {
      setLoading(true);
      const response = await processedItemsService.listProcessedItems({
        searchQuery: search,
        sortField,
        sortOrder,
        pageIndex: page,
        pageSize,
        ...filters,
      });
      setProcessedItems(response.data);
      setTotalItems(response.metadata.total.value);
    } catch (error) {
      toast.error("Failed to load processed items");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateItem = () => {
    setSelectedItem(null);
    setFormDialogOpen(true);
  };

  const handleEditItem = (item: IESDoc) => {
    setSelectedItem(item as ProcessedItemDoc);
    setFormDialogOpen(true);
  };

  const handleDeleteItem = async () => {
    if (!selectedItem) return;
    try {
      setSaving(true);
      await processedItemsService.deleteProcessedItem(selectedItem._id);
      toast.success("Processed item deleted successfully");
      handleCloseDeleteDialog();
      loadProcessedItems();
    } catch (error) {
      toast.error("Failed to delete processed item");
      console.error("Error deleting processed item:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDeleteDialog = (item: IESDoc) => {
    setSelectedItem(item as ProcessedItemDoc);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setSelectedItem(null);
    setDeleteDialogOpen(false);
  };

  const handleCloseFormDialog = () => {
    setSelectedItem(null);
    setFormDialogOpen(false);
  };

  const handleSort = (field: string) => {
    if (field === sortField) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handleDetailsOpen = (item: IESDoc) => {
    setSelectedItem(item as ProcessedItemDoc);
    setDetailsDrawerOpen(true);
  };

  const handleDetailsClose = () => {
    setSelectedItem(null);
    setDetailsDrawerOpen(false);
  };

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Assembly of Pantry Items"
        subtitle="Manage your kitchen's processed and assembled items"
        icon="lunch_dining"
        actions={
          <Button
            variant="contained"
            startIcon={<Icon>add</Icon>}
            onClick={handleCreateItem}
            disabled={
              !hasPermission(
                "inventory.api.create",
                user?.newPermissions,
                user?.assignment_details?.id,
                user?.is_store_owner
              )
            }
            sx={{
              bgcolor: "#0891B2",
              "&:hover": {
                bgcolor: "#0E7490",
              },
            }}
          >
            Add Processed Item
          </Button>
        }
      />

      <Paper
        sx={{
          p: 3,
          mb: 3,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid",
          borderColor: alpha("#0891B2", 0.1),
        }}
        elevation={0}
      >
        <AppSearch
          placeholder="Search processed items by name or description..."
          maxWidth="100%"
          onSearch={setSearch}
          onFilterChange={setFilters}
          filterGroups={filterGroups}
          defaultFilters={filters}
        />
      </Paper>

      <Paper
        sx={{
          p: 3,
          mb: 3,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid",
          borderColor: alpha("#0891B2", 0.1),
        }}
        elevation={0}
      >
        <AppTable
          data={processedItems}
          columns={MasterColumns}
          loading={loading}
          sortConfig={{ field: sortField, direction: sortOrder }}
          onSort={handleSort}
          pagination={{
            total: totalItems,
            page: page,
            pageSize: pageSize,
            onPageChange: setPage,
            onPageSizeChange: setPageSize,
          }}
          header={{
            title: "Processed Items List",
            subtitle: "Your current list of processed and assembled items",
          }}
          onRowClick={(row) => handleDetailsOpen(row as ProcessedItemDoc)}
          styleOptions={{
            bordered: true,
            columnSeparators: true,
            headerBackground: alpha("#0891B2", 0.02),
            rowHoverBackground: alpha("#0891B2", 0.04),
            borderColor: alpha("#0891B2", 0.1),
          }}
          toolbarActions={{
            refresh: {
              label: "Refresh List",
              is_disabled: loading,
              onClick: loadProcessedItems,
            },
            export: {
              label: "Export Items",
              is_disabled: loading,
              options: {
                formats: ["csv", "excel", "json"],
                filename: "processed-items-list",
              },
              formatter: (data: any) => {
                return data.map((item: any) => ({
                  ...item,
                  ingredients: item.ingredients.map(
                    (ingredient: any) => ingredient.name
                  ),
                }));
              },

              datasource: "apis",
            },
          }}
          emptyState={{
            icon: "lunch_dining",
            title: "No Processed Items Found",
            description: search
              ? "No items match your search criteria. Try adjusting your filters or search term."
              : "Get started by adding your first processed item to manage your kitchen inventory effectively.",
            actionLabel: "Add Processed Item",
            onAction: handleCreateItem,
          }}
        />
      </Paper>

      {/* Form Dialog */}
      {formDialogOpen && (
        <ProcessedItemFormDialog
          open={formDialogOpen}
          onClose={handleCloseFormDialog}
          onSave={loadProcessedItems}
          selectedDoc={selectedItem}
          mode={selectedItem ? "edit" : "add"}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleDeleteItem}
        title="Delete Processed Item"
        itemName={selectedItem?.name || ""}
        itemDescription={selectedItem?.description || ""}
        loading={saving}
        icon="lunch_dining"
      />

      {/* Details Drawer */}
      {detailsDrawerOpen && selectedItem && (
        <ProcessedItemDetailsDrawer
          open={detailsDrawerOpen}
          onClose={handleDetailsClose}
          processedItem={selectedItem}
        />
      )}
    </Box>
  );
};
